/***************************************************************************************
 * FileName      : Portal.tsx
 * Description   : KETRI 디지털트윈 데모 카탈로그 — 메인 진입 페이지 + 서브패스 라우팅.
 *                 - '/'  : 카드 그리드 (PROJECTS 배열)
 *                 - '/<subpath>' (예: '/scs-home-app') : 해당 프로젝트를 iframe 으로 임베드
 *                 - 카드 클릭 시 pushState 로 URL 만 갱신 (전체 reload 없이 iframe 전환)
 *                 - 브라우저 뒤로가기/앞으로가기 popstate 로 동기화
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.04.27
 * Modified Date : 2026.04.28 라이트 테마 + 서브패스 라우팅 + iframe 임베드
 ***************************************************************************************/

import * as React from 'react';
import { PROJECTS, ProjectEntry, resolveAppUrl } from './projects';

interface CardProps {
  project: ProjectEntry;
  onNavigate: (subpath: string) => void;
}

const ProjectCard: React.FC<CardProps> = ({ project, onNavigate }) => {
  const accent = project.accentColor || '#1e3a8a';
  return (
    <a
      className="card"
      href={project.subpath}
      style={{ ['--card-accent' as any]: accent }}
      onClick={(e) => {
        // 좌클릭 + 보조키 없을 때만 SPA 전환 (Ctrl/Cmd-클릭 등은 새 탭 기본 동작 유지)
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onNavigate(project.subpath);
      }}
    >
      {project.badge && <span className="card__badge">{project.badge}</span>}
      {project.subtitle && <p className="card__subtitle">{project.subtitle}</p>}
      <h2 className="card__title">{project.title}</h2>
      <p className="card__description">{project.description}</p>
      <span className="card__action">바로가기</span>
    </a>
  );
};

// 2026-08-27 : launcher → ketri-portal 개명 시 최상위 컴포넌트가 Portal 이 되면서 이름이 겹쳐
//   카드 그리드 화면은 PortalHome 으로 분리.
const PortalHome: React.FC<{ onNavigate: (s: string) => void }> = ({ onNavigate }) => (
  <div className="portal">
    <header className="portal__header">
      <div className="portal__brand">
        <span className="portal__brand-mark">KETRI</span>
        <span className="portal__brand-name">(주)한국공학기술연구원</span>
      </div>
      <h1 className="portal__title">3D 스캔 기술로 구현한 실감형 공간 체험</h1>
      <p className="portal__lead">
        현장을 직접 방문하지 않고도 실제와 동일한 공간을 탐색할 수 있습니다. 관심 있는 데모를 선택해 체험해 보세요.
      </p>
    </header>

    <main className="portal__grid">
      {PROJECTS.map((p) => (
        <ProjectCard key={p.id} project={p} onNavigate={onNavigate} />
      ))}
    </main>

    <footer className="portal__footer">© 2026 (주)한국공학기술연구원</footer>
  </div>
);

interface EmbedProps {
  project: ProjectEntry;
  onBack: () => void;
}

const Embed: React.FC<EmbedProps> = ({ project, onBack }) => {
  const src = resolveAppUrl(project);
  return (
    <div className="embed">
      {/* 2026.04.28 : 뒤로가기 버튼은 임베드된 각 앱이 자체 헤더에 렌더하므로 ketri-portal 의 floating 바 제거 */}
      <iframe
        className="embed__frame"
        src={src}
        title={project.title}
        // 매터포트/카메라/마이크 등 일반적으로 필요한 권한 허용 (fullscreen 포함이라 allowFullScreen 속성 불필요)
        allow="autoplay; fullscreen; xr-spatial-tracking; camera; microphone"
      />
    </div>
  );
};

const findProjectBySubpath = (pathname: string): ProjectEntry | undefined => {
  // 끝 슬래시 정규화
  const norm = pathname.replace(/\/+$/, '') || '/';
  return PROJECTS.find((p) => p.subpath === norm);
};

export const Portal: React.FC = () => {
  const [pathname, setPathname] = React.useState<string>(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  React.useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // 알 수 없는 경로(예: /foobar)로 진입 시 조용히 '/'로 정정 (히스토리 항목 추가하지 않음)
  React.useEffect(() => {
    const norm = pathname.replace(/\/+$/, '') || '/';
    if (norm !== '/' && !findProjectBySubpath(norm)) {
      window.history.replaceState({}, '', '/');
      setPathname('/');
    }
  }, [pathname]);

  const navigate = React.useCallback((subpath: string) => {
    if (window.location.pathname !== subpath) {
      window.history.pushState({}, '', subpath);
    }
    setPathname(subpath);
  }, []);

  const goBack = React.useCallback(() => navigate('/'), [navigate]);

  // 임베드된 앱이 자체 헤더의 ← 버튼을 누르면 postMessage 로 신호를 보냄. 받아서 메인으로 복귀.
  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === 'portal:back') {
        goBack();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [goBack]);

  const project = findProjectBySubpath(pathname);
  if (project) return <Embed project={project} onBack={goBack} />;
  return <PortalHome onNavigate={navigate} />;
};
